import omitDeep from "omit-deep-lodash";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Header from "shared/components/Header";
import ProcessStepper from "shared/components/ProcessStepper";
import Loading from "shared/components/Spin";
import { useLazyQuery, useMutation, useQuery } from "shared/hooks/useApi";
import graphql from "utils/api/graphql";

import cuid from "cuid";
import { defaults, defaultsDeep, defaultTo, get, keyBy } from "lodash";
import { useTranslation } from "react-i18next";
import { NAME_SPACES } from "shared/locales/constants";
import { GENERAL_COST } from "utils/api/graphql/queries/general-cost";
import { CONTACT_POINT_SYSTEM, SUBSCRIPTION_STATUSES } from "utils/constants";
import View from "./View";

const steps = [
  { title: "Qualification" },
  { title: "Sélection<br />produits" },
  { title: "Devis" },
  { title: "Souscription" },
  { title: "Documents" },
  { title: "Signature<br />En attente" },
  { title: "Souscription terminée" },
];
const STEPPER_KEYS = {
  SUBSCRIPTION: "SUBSCRIPTION",
  DOCUMENT: "DOCUMENT",
  SUBSCRIPTION_DONE: "SUBSCRIPTION_DONE",
};

export const SUBSCRIPTION_STEPS = {
  INITIAL: "INITIAL",
  VIEW_DOCUMENTS: "VIEW_DOCUMENTS",
  SIGNATURE_WAITING: "SIGNATURE_WAITING",
  SUBSCRIPTION_DONE: "SUBSCRIPTION_DONE",
};

export const STATUS_TO_STEP = {
  INITIAL: 3,
  VIEW_DOCUMENTS: 4,
  SIGNATURE_WAITING: 5,
  SUBSCRIPTION_DONE: 6,
};

const Detail = () => {
  const [getGeneralCost] = useLazyQuery(GENERAL_COST);
  const { id } = useParams();
  const [currentStep, setCurrentStep] = useState(null);
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.SUBSCRIPTION);
  const [loading, setLoading] = useState(true);
  const [totalPrice, setTotalPrice] = useState(0);
  const TRANSLATION = t("DETAIL.LAFOURGALE", {
    returnObjects: true,
  });
  const [initialValues, setInitialValues] = useState({});
  const [updateSubscription] = useMutation(
    graphql.mutations.UPDATE_SUBSCRIPTION,
    {
      refetchQueries: [
        {
          query: graphql.queries.SUBSCRIPTION,
          awaitRefetchQueries: true,
          variables: { where: { id } },
        },
      ],
    },
  );

  const SUBSCRIPTION_TO_STATUS_MAP = {
    [SUBSCRIPTION_STATUSES.PENDING]: (subscription) =>
      get(subscription, "additionalInfo.currentStep") ===
      SUBSCRIPTION_STEPS.VIEW_DOCUMENTS
        ? SUBSCRIPTION_STEPS.VIEW_DOCUMENTS
        : SUBSCRIPTION_STEPS.SIGNATURE_WAITING,
    [SUBSCRIPTION_STATUSES.SIGNED]: () => SUBSCRIPTION_STEPS.SUBSCRIPTION_DONE,
    [SUBSCRIPTION_STATUSES.DRAFT]: (subscription) =>
      !!get(subscription, "additionalInfo.generatedDocuments")
        ? SUBSCRIPTION_STEPS.VIEW_DOCUMENTS
        : undefined,
    [SUBSCRIPTION_STATUSES.IN_PROGRESS]: () => undefined,
  };

  const initSubscription = useCallback(({ subscription: data }) => {
    const subscription = omitDeep(data, "__typename");
    setCurrentStep(
      SUBSCRIPTION_TO_STATUS_MAP[get(subscription, "status")](subscription) ||
        get(
          subscription,
          "additionalInfo.currentStep",
          SUBSCRIPTION_STEPS.INITIAL,
        ),
    );
    getGeneralCost({
      variables: {
        where: {
          project: { id: get(subscription, "project.id") },
          insurancePlan: { id: get(subscription, "insurancePlan.id") },
        },
      },
      onCompleted: ({ generalCost: generalCostData }) => {
        const subscriptionPayment = get(subscription, "payment");
        const isAdherent = get(
          subscription,
          "payment.payer.owner.isAdherent",
          true,
        );
        const telecoms = get(subscription, "project.contact.telecoms", []);
        const consent = get(subscription, "project.fields.consent", {});
        const totalPrice = get(
          subscription,
          `project.fields.upsells.${get(subscription, "insurancePlan.id")}`,
          [],
        ).reduce(
          (acc, { cost }) => acc + cost,
          get(generalCostData, "cost", 0),
        );
        setTotalPrice(totalPrice);
        setInitialValues({
          payment: {
            id: cuid(),
            ...subscriptionPayment,
            payer: {
              id: cuid(),
              IBAN: null,
              BIC: null,
              ...subscription.payment?.payer,
              owner: {
                id: cuid(),
                ...subscription.payment?.payer?.owner,
                firstName: defaultTo(
                  subscription.payment?.payer?.owner?.firstName,
                  isAdherent
                    ? get(subscription, "project.contact.user.firstname")
                    : null,
                ),
                lastName: defaultTo(
                  subscription.payment?.payer?.owner?.lastName,
                  isAdherent
                    ? get(subscription, "project.contact.user.lastname")
                    : null,
                ),
                isAdherent: defaultTo(
                  subscription.payment?.payer?.owner?.isAdherent,
                  true,
                ),
              },
            },
            receiver: defaultsDeep(subscription.payment?.receiver, {
              id: cuid(),
              IBAN: null,
              BIC: null,
              owner: { id: cuid() },
            }),
          },
          contactTelecoms: defaults(keyBy(telecoms, "system"), {
            [CONTACT_POINT_SYSTEM.ZIP_CODE]: { id: cuid(), value: null },
            [CONTACT_POINT_SYSTEM.CITY]: { id: cuid(), value: null },
            [CONTACT_POINT_SYSTEM.ADDRESS]: { id: cuid(), value: null },
            [CONTACT_POINT_SYSTEM.EMAIL]: { id: cuid(), value: null },
            [CONTACT_POINT_SYSTEM.PHONE]: { id: cuid(), value: null },
          }),
          consent: {
            optinMarketing: get(consent, "optinMarketing", false),
            optinPartners: get(consent, "optinPartners", false),
            acceptCGV: get(consent, "acceptCGV", false),
            acceptSepa: get(consent, "acceptSepa", false),
          },
        });
        setLoading(false);
      },
    });
  }, []);

  const { data, error } = useQuery(graphql.queries.SUBSCRIPTION, {
    variables: { where: { id } },
    onCompleted: initSubscription,
  });

  const [active, setActive] = useState(STEPPER_KEYS.SUBSCRIPTION);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [active]);

  if (loading) return <Loading />;

  return (
    <>
      <Header key="header" title={"Souscription"} actions={[]} />
      <ProcessStepper steps={steps} current={STATUS_TO_STEP[currentStep]} />
      <View
        active={active}
        setActive={setActive}
        data={omitDeep(data, "__typename")}
        translation={TRANSLATION}
        initialValues={initialValues}
        updateSubscription={updateSubscription}
        totalPrice={totalPrice}
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
      />
    </>
  );
};

export default Detail;
